/**
 * MobileNotificationsPanel Component
 *
 * Full-screen slide-in panel for mobile notification viewing.
 * Slides in from the right side of the screen.
 */

import { useEffect } from 'react';
import { NotificationItem } from './NotificationItem';
import { NotificationEmptyState } from './NotificationEmptyState';
import type { UnifiedNotification } from '../../hooks/useNotifications';
import styles from './NotificationDropdown.module.css';

interface MobileNotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  notifications: UnifiedNotification[];
  isLoading: boolean;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  onAcceptInvite: (id: string) => Promise<void>;
  onDeclineInvite: (id: string) => Promise<void>;
  onAcceptTrade: (id: string) => Promise<void>;
  onDeclineTrade: (id: string) => Promise<void>;
  onViewTrade?: (tradeId: string) => void;
}

export function MobileNotificationsPanel({
  isOpen,
  onClose,
  notifications,
  isLoading,
  onMarkAsRead,
  onMarkAllAsRead,
  onAcceptInvite,
  onDeclineInvite,
  onAcceptTrade,
  onDeclineTrade,
  onViewTrade
}: MobileNotificationsPanelProps) {
  const hasUnread = notifications.some(n => !n.isRead);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleAction = async (
    notification: UnifiedNotification,
    actionType: 'accept' | 'decline' | 'view'
  ) => {
    if (notification.type === 'invite') {
      if (actionType === 'accept') {
        await onAcceptInvite(notification.id);
      } else if (actionType === 'decline') {
        await onDeclineInvite(notification.id);
      }
    } else if (notification.type === 'trade') {
      if (actionType === 'accept') {
        await onAcceptTrade(notification.id);
      } else if (actionType === 'decline') {
        await onDeclineTrade(notification.id);
      } else if (actionType === 'view' && onViewTrade) {
        const tradeId = notification.id.replace('trade-', '');
        onViewTrade(tradeId);
        onClose();
      }
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`${styles.mobileBackdrop} ${isOpen ? styles.mobileBackdropOpen : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className={`${styles.mobilePanel} ${isOpen ? styles.mobilePanelOpen : ''}`}
        role="dialog"
        aria-label="Notifications"
        aria-modal="true"
      >
        <div className={styles.mobilePanelHeader}>
          <h2 className={styles.mobilePanelTitle}>Notifications</h2>
          <div className={styles.mobilePanelActions}>
            {hasUnread && (
              <button
                className={styles.markAllRead}
                onClick={onMarkAllAsRead}
                disabled={isLoading}
              >
                Mark all read
              </button>
            )}
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close notifications"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        <div className={styles.mobilePanelContent}>
          {notifications.length === 0 ? (
            <NotificationEmptyState />
          ) : (
            notifications.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={() => onMarkAsRead(notification.id)}
                onAccept={() => handleAction(notification, 'accept')}
                onDecline={() => handleAction(notification, 'decline')}
                onView={() => handleAction(notification, 'view')}
                isLoading={isLoading}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
}
